import {Component, computed, effect, inject, input, OnInit, viewChild} from '@angular/core';
import {AutoUnsubscribe} from 'src/infrastructure/decorators/auto-unsubscribe.decorator';
import {MatSnackBar} from '@angular/material/snack-bar';
import {DatePipe} from '@angular/common';
import {AdxInvoice} from '../../model/adx-invoice';
import {AdxInvoiceService} from '../../service/adx-invoice.service';
import {SearchPaginatedAdxInvoiceResponse} from '../../model/search-paginated-adx-invoice-response';
import {MatDialog} from '@angular/material/dialog';
import {HistoryComponent} from 'src/app/history/components/history/history.component';
import {PaymentFormComponent} from 'src/app/payments/components/payment-form/payment-form.component';
import {AzureAuthService}  from 'src/infrastructure/services/azure-auth.service';
import {AdxPaymentService} from 'src/app/payments/service/adx-payment.service';
import {AdxInvoiceDetailsModalComponent} from '../adx-invoice-details-modal/adx-invoice-details-modal.component';
import {InvoiceFormStepperComponent} from 'src/app/invoices/component/invoice-form-stepper/invoice-form-stepper.component';
import {GridEventOutput, GridModel} from 'src/app/shared/model/page-grid';
import {AdxInvoiceGridService} from '../../service/adx-invoice-grid.service';
import {PageGridComponent} from 'src/app/shared/components/page-grid/page-grid.component';

@Component({
    selector: 'adx-invoice-list',
    templateUrl: './adx-invoice-list.component.html',
    standalone: false
})

@AutoUnsubscribe()
export class AdxInvoiceListComponent implements OnInit {
  public data = input<any>();
  pageGrid = viewChild(PageGridComponent);

  private adxInvoiceGridService = inject(AdxInvoiceGridService)
  private azureAuthService = inject(AzureAuthService);
  private adxInvoiceService = inject(AdxInvoiceService);
  private adxPaymentService = inject(AdxPaymentService);
  private datePipe = inject(DatePipe);
  private snackBar = inject(MatSnackBar);
  private dialog = inject(MatDialog);

  constructor() {
    effect(() => {
      const dataValue = this.data();
      const hasRole = this.loggedInUserHasDirectorOrFinanceOrManagerRole();
      const dataSourceId = dataValue?.trip?.dataSourceId;
      
      if (hasRole) {
        if (dataSourceId != null && dataSourceId > 0) {
          this.adxInvoicesGrid.eventButton = { text: 'Add', icon: 'add' };
        } else {
          this.adxInvoicesGrid.eventButton = { 
            text: 'Add', 
            icon: 'add', 
            disabled: true,
            tooltip: 'Cannot create invoice - no datasource available for this trip'
          };
        }
      } else {
        this.adxInvoicesGrid.eventButton = undefined;
      }
    });
  }

  protected readonly loggedInUserHasDirectorOrFinanceOrManagerRole = computed(() => {
    const hasRole = this.azureAuthService.checkUserAzureTokenHasManagerRole() && this.azureAuthService.checkUserAzureTokenHasFinanceRole() && this.azureAuthService.checkUserAzureTokenHasDirectorRole();
    return hasRole;
  });

  protected readonly shouldShowAddButton = computed(() => {
    const dataValue = this.data();
    const hasRole = this.loggedInUserHasDirectorOrFinanceOrManagerRole();
    const dataSourceId = dataValue?.trip?.dataSourceId;
    const shouldShow = hasRole && dataSourceId != null && dataSourceId > 0;
    return shouldShow;
  });

  protected readonly adxInvoicesGrid: GridModel<SearchPaginatedAdxInvoiceResponse> = {
    gridDataTestId: 'adx-invoice-list',
    gridTitle: 'Invoices',
    replayButton: true,
    showActionColumn: true,
    eventButton: undefined,
    isTotalsRowHidden: true,
    defaultSort: { active: 'id', direction: 'asc' },
    columns: [
      {
        columnProperty: 'id',
        columnHeader: 'Invoice Number',
        sort: true,
        clickable: true,
        style: () => ({ 'color': '#0078d4', 'text-decoration': 'underline', 'cursor': 'pointer' }),
        linkEventFunction: (item: AdxInvoice) => `AdxInvoices/${item.id}`,
      },
      {
        columnProperty: 'recordLocator',
        columnHeader: 'Invoice Record Locator',
        sort: true,
      },
      {
        columnProperty: 'branch.name',
        columnHeader: 'Branch Name',
        columnPropertyFunction: (item: AdxInvoice) => item.branch?.name || '',
        sort: true,
      },
      {
        columnProperty: 'client.profileName',
        columnHeader: 'Client Name',
        columnPropertyFunction: (item: AdxInvoice) => item.client?.profileName || '',
        sort: true,
      },
      {
        columnProperty: 'invoiceTypeCodeName',
        columnHeader: 'Type',
      },
      {
        columnProperty: 'invoiceGroup',
        columnHeader: 'Invoice Group',
        sort: true,
      },
      {
        columnProperty: 'issueDate',
        columnHeader: 'Issue Date',
        columnPropertyFunction: (item: AdxInvoice) => this.datePipe.transform(item.issueDate, 'M/d/y') || '',
        sort: true,
      },
      {
        columnProperty: 'totalAmount',
        columnHeader: 'Total Amount',
        columnPropertyFunction: (item: AdxInvoice) =>  item.totalAmount != null ? `$${item.totalAmount.toFixed(2)}` : '',
        sort: true,
      },
      {
        columnProperty: 'partPayAmt',
        columnHeader: 'Voucher Amount',
        columnPropertyFunction: (item: AdxInvoice) =>  item.partPayAmt != null ? `$${item.partPayAmt.toFixed(2)}` : '',
        sort: true,
      },
    ],
    filters: [],
    gridGetService: this.adxInvoiceGridService
  }

  public ngOnInit(): void {
    const dataValue = this.data();
    if (dataValue == undefined) {
    }

    this.adxInvoicesGrid.filtersInitial = {
      tripId: dataValue?.tripId || null,
    }
  }

  handleInvoiceHistoryClick(adxInvoiceId : number){
    this.adxInvoiceService.getInvoiceHistoryById(adxInvoiceId)
      .subscribe((data: any) => {
        if(data.length == 0){
          this.snackBar.open('No Invoice history found for Id: ' + adxInvoiceId, 'Dismiss', {
            horizontalPosition: 'center',
            verticalPosition: 'bottom',
            panelClass: ['message-position', 'app-notification-success']
          });
        }
        else
        {
          let dialogConfirmationRef =
            this.dialog.open(HistoryComponent, {
              width: '60%',
              data: {
                mode: 'Invoice',
                historyData: data,
                id: adxInvoiceId,
              }
          })
          dialogConfirmationRef.afterClosed().subscribe(
            (result: boolean) => {
              if (result != null && result){
              }
            }
          );
        }
      });
  }

  handleInvoicePayments(adxInvoice : AdxInvoice){
    if(adxInvoice){
        const modalRef = this.dialog.open(PaymentFormComponent, {
            data: {
              adxInvoiceId:adxInvoice.id,
              recordLocator: adxInvoice.recordLocator,
              dataSourceId: adxInvoice.dataSourceId,
            },
        });
      modalRef.afterClosed().subscribe((created)=>{
            if(created){
              this.adxPaymentService.triggerAdxPaymentGridRefresh();
            }
        });
    }
  }

  handleIdClick(event: GridEventOutput<AdxInvoice>): void {
    if(event.column === 'id' && event.item.id !== undefined){
      this.openDialog(event.item.id, `Invoice Information`);
    }
  }

  public openDialog(adxInvoiceId: number, title: string): void {
    this.dialog.open(AdxInvoiceDetailsModalComponent, {
      width: '100%',
      maxWidth: '95vw',
      data: { adxInvoiceId: adxInvoiceId, title: title }
    });
  }

  protected handleCreateInvoiceDialog(): void {
    const tripData = this.data();
    this.dialog
      .open(InvoiceFormStepperComponent, {
        data: tripData?.trip
      })
      .afterClosed()
      .subscribe((saved) => {
        if (saved) {
          this.pageGrid()?.getItems();
        }
      });
  }
}
